import * as React from 'react';
import { useState, useEffect } from 'react';

import { SchoolInfoFormProps, SchoolOption } from './types';

import SchoolNameAutocomplete from './SchoolNameAutocomplete';

const SchoolInfoForm: React.FC<SchoolInfoFormProps> = ({
  currentStatus: initialCurrentStatus = '',
  schoolType: initialSchoolType = '',
  schoolMasterId: initialSchoolMasterId,
  schoolName: initialSchoolName = '',
  schoolNameFree: initialSchoolNameFree = '',
  selectedSchool: initialSelectedSchool = null,
  majorSubjectId: initialMajorSubjectId,
  graduationYear: initialGraduationYear,
  majorSubjects = [],
  onCurrentStatusChange,
  onSchoolTypeChange,
  onSchoolMasterChange,
  onSchoolNameFreeChange,
  onMajorSubjectChange,
  onGraduationYearChange,
  canEditSchoolInfo = true,
  errors = {}
}) => {
  const [currentStatus, setCurrentStatus] = useState<string>(initialCurrentStatus);
  const [schoolType, setSchoolType] = useState<string>(initialSchoolType);
  const [schoolMasterId, setSchoolMasterId] = useState<number | undefined>(initialSchoolMasterId);
  const [schoolName, setSchoolName] = useState<string>(initialSchoolName);
  const [schoolNameFree, setSchoolNameFree] = useState<string>(initialSchoolNameFree);
  const [graduationYear, setGraduationYear] = useState<number | undefined>(initialGraduationYear);
  const [selectedMajorSubject, setSelectedMajorSubject] = useState<number | undefined>(initialMajorSubjectId);
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(initialSelectedSchool);

  const isEnrolled = currentStatus === '1';
  const isPredictableSchoolType = ['1', '2', '3'].includes(schoolType);

  const selectedMajorSubjectObj = majorSubjects.find((ms) => ms.id === selectedMajorSubject);
  const topLevelMajorSubjects = majorSubjects.filter((ms) => !ms.parent_category);
  const otherMajorSubjects = majorSubjects.filter((ms) => ms.category === 'other' && ms.parent_category);
  const otherParentId = majorSubjects.find((ms) => ms.category === 'other' && !ms.parent_category)?.id;
  const isSubCategory = selectedMajorSubjectObj?.category === 'other' && selectedMajorSubjectObj?.parent_category;
  const isMajorSubjectOther =
    selectedMajorSubjectObj?.category === 'other' && !selectedMajorSubjectObj?.parent_category;
  const topLevelValue = isSubCategory ? otherParentId : selectedMajorSubject;

  useEffect(() => {
    setCurrentStatus(initialCurrentStatus);
    setSchoolType(initialSchoolType);
    setSchoolMasterId(initialSchoolMasterId);
    setSchoolName(initialSchoolName);
    setSchoolNameFree(initialSchoolNameFree);
    setGraduationYear(initialGraduationYear);
    setSelectedMajorSubject(initialMajorSubjectId);
    setSelectedSchool(initialSelectedSchool);
  }, [
    initialCurrentStatus,
    initialSchoolType,
    initialSchoolMasterId,
    initialSchoolName,
    initialSchoolNameFree,
    initialGraduationYear,
    initialMajorSubjectId,
    initialSelectedSchool
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.updateHiddenFields) {
      // majorSubjectDetailId: set to selectedMajorSubject if detail is selected (isSubCategory),
      // set to empty string if "Other" is selected but detail is not (isMajorSubjectOther),
      // otherwise undefined (not visible)
      let majorSubjectDetailId: number | string | undefined = undefined;
      if (isSubCategory) {
        majorSubjectDetailId = selectedMajorSubject;
      } else if (isMajorSubjectOther) {
        majorSubjectDetailId = '';
      }

      window.updateHiddenFields({
        currentStatus: currentStatus,
        schoolType: schoolType,
        schoolMasterId: schoolMasterId,
        schoolName: schoolName,
        schoolNameFree: schoolNameFree,
        majorSubjectId: selectedMajorSubject,
        majorSubjectDetailId: majorSubjectDetailId,
        graduationYear: graduationYear
      });
    }
  }, [
    currentStatus,
    schoolType,
    schoolMasterId,
    schoolName,
    schoolNameFree,
    selectedMajorSubject,
    graduationYear,
    isSubCategory,
    isMajorSubjectOther
  ]);

  const handleCurrentStatusChange = (value: string) => {
    setCurrentStatus(value);
    onCurrentStatusChange?.(value);
  };

  const handleSchoolTypeChange = (value: string) => {
    setSchoolType(value);
    setSchoolMasterId(undefined);
    setSchoolName('');
    setSchoolNameFree('');
    setSelectedSchool(null);
    onSchoolTypeChange?.(value);
  };

  const handleSchoolChange = (option: SchoolOption | null) => {
    setSelectedSchool(option);
    if (option) {
      setSchoolMasterId(option.value);
      setSchoolName(option.label);
      onSchoolMasterChange?.(option.value, option.label);
    } else {
      setSchoolMasterId(undefined);
      setSchoolName('');
      onSchoolMasterChange?.(0, '');
    }
  };

  const handleSchoolNameFreeChange = (value: string) => {
    setSchoolNameFree(value);
    onSchoolNameFreeChange?.(value);
  };

  const handleMajorSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const subject = majorSubjects.find((ms) => ms.id === id);
    setSelectedMajorSubject(id);

    if (subject && subject.category === 'other' && !subject.parent_category) {
      onMajorSubjectChange?.(0);
    } else {
      onMajorSubjectChange?.(id);
    }
  };

  const handleMajorSubjectDetailChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setSelectedMajorSubject(id);
    onMajorSubjectChange?.(id);
  };

  const handleGraduationYearChange = (year: number) => {
    setGraduationYear(year);
    onGraduationYearChange?.(year);
  };

  const graduationYearOptions = (() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 0-indexed to 1-indexed

    // 9月以前なら当年から、10月以降なら翌年から
    const startOffset = currentMonth <= 9 ? 0 : 1;

    const years = [];
    for (let i = startOffset; i <= startOffset + 5; i++) {
      const year = currentYear + i;
      years.push({ value: year, label: `${year}年3月卒 (${String(year).slice(-2)}卒)` });
    }
    return years;
  })();

  return (
    <>
      <p className="q-title">
        <span className="hissu">必須</span>現在のご状況を選択してください。
      </p>
      <div className="q-contents">
        <select
          id="registration_status"
          name="registration_status"
          value={currentStatus}
          onChange={(e) => handleCurrentStatusChange(e.target.value)}
          disabled={!canEditSchoolInfo}
          className={errors.currentStatus && errors.currentStatus.length > 0 ? 'ipt-error' : ''}
        >
          <option value="">在学/就業状況を選択</option>
          <option value="1">在学中（学生の方）</option>
          <option value="2">在職中</option>
          <option value="3">離職中</option>
          <option value="4">その他</option>
        </select>
        {errors.currentStatus && errors.currentStatus.length > 0 && (
          <p className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
            {errors.currentStatus[0]}
          </p>
        )}
      </div>

      {isEnrolled && (
        <>
          <p className="q-title">
            <span className="hissu">必須</span>学校種別を選択してください。
          </p>
          <div className="q-contents">
            <select
              id="school_type"
              name="school_type"
              value={schoolType}
              onChange={(e) => handleSchoolTypeChange(e.target.value)}
              disabled={!canEditSchoolInfo}
              className={errors.schoolType && errors.schoolType.length > 0 ? 'ipt-error' : ''}
            >
              <option value="">学校種別を選択</option>
              <option value="1">大学・大学院</option>
              <option value="2">短期大学</option>
              <option value="3">農業大学校</option>
              <option value="4">専門学校</option>
              <option value="5">高等学校・高専</option>
              <option value="6">その他</option>
            </select>
            {errors.schoolType && errors.schoolType.length > 0 && (
              <p className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                {errors.schoolType[0]}
              </p>
            )}
          </div>

          {schoolType && (
            <>
              <p className="q-title">
                <span className="hissu">必須</span>学校名を入力してください。
              </p>
              {isPredictableSchoolType && <p className="q-note">※入力すると候補が表示されます</p>}
              <div className="q-contents q-contents-full">
                {!isPredictableSchoolType ? (
                  <input
                    type="text"
                    id="school_name_free"
                    name="school_name_free"
                    value={schoolNameFree || ''}
                    onChange={(e) => handleSchoolNameFreeChange(e.target.value)}
                    placeholder="例) 〇〇高等学校"
                    disabled={!canEditSchoolInfo}
                    className={errors.schoolName && errors.schoolName.length > 0 ? 'ipt-error' : ''}
                    maxLength={255}
                  />
                ) : (
                  <SchoolNameAutocomplete
                    schoolType={schoolType}
                    value={selectedSchool}
                    onChange={handleSchoolChange}
                    isDisabled={!canEditSchoolInfo}
                    hasError={errors.schoolName && errors.schoolName.length > 0}
                  />
                )}
                {errors.schoolName && errors.schoolName.length > 0 && (
                  <p className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                    {errors.schoolName[0]}
                  </p>
                )}
              </div>
            </>
          )}

          {isPredictableSchoolType && (
            <>
              <p className="q-title">
                <span className="hissu">必須</span>専攻科目を選択してください。
              </p>
              <div className="q-contents">
                <select
                  id="major_subject_id"
                  name="major_subject_id"
                  value={topLevelValue || ''}
                  onChange={handleMajorSubjectChange}
                  disabled={!canEditSchoolInfo}
                  className={errors.majorSubjectId && errors.majorSubjectId.length > 0 ? 'ipt-error' : ''}
                >
                  <option value="">専攻科目を選択</option>
                  {topLevelMajorSubjects.map((ms) => (
                    <option key={ms.id} value={ms.id}>
                      {ms.displayName}
                    </option>
                  ))}
                </select>
                {errors.majorSubjectId && errors.majorSubjectId.length > 0 && (
                  <p className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                    {errors.majorSubjectId[0]}
                  </p>
                )}
              </div>

              {(isMajorSubjectOther || isSubCategory) && (
                <>
                  <p className="q-title">
                    <span className="hissu">必須</span>専攻科目詳細を選択してください。
                  </p>
                  <div className="q-contents">
                    <select
                      id="major_subject_detail_id"
                      name="major_subject_detail_id"
                      value={isSubCategory ? selectedMajorSubject || '' : ''}
                      onChange={handleMajorSubjectDetailChange}
                      disabled={!canEditSchoolInfo}
                    >
                      <option value="">専攻科目詳細を選択</option>
                      {otherMajorSubjects.map((ms) => (
                        <option key={ms.id} value={ms.id}>
                          {ms.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </>
          )}

          <p className="q-title">
            <span className="hissu">必須</span>卒業見込みを選択してください。
          </p>
          <p className="q-note">※卒業見込みが9月の場合は、同年度の「◯年3月卒」を選択してください。</p>
          <div className="q-contents">
            <select
              id="graduation_year"
              name="graduation_year"
              value={graduationYear || ''}
              onChange={(e) => handleGraduationYearChange(Number(e.target.value))}
              disabled={!canEditSchoolInfo}
              className={errors.graduationYear && errors.graduationYear.length > 0 ? 'ipt-error' : ''}
            >
              <option value="">選択してください</option>
              {graduationYearOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.graduationYear && errors.graduationYear.length > 0 && (
              <p className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                {errors.graduationYear[0]}
              </p>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default SchoolInfoForm;
